import { useEffect, useState } from 'react'
import { useWatchlist } from '@/hooks/useWatchlist'
import DataSection from '@/components/ui/DataSection'
import ExportCSV, { type ColumnDef } from '@/components/ui/ExportCSV'
import type { AlertCondition, WatchlistItem } from '@/lib/types'
import { addToWatchlist } from '@/lib/api'
import {
  Eye, Plus, Trash2, Bell, BellOff, X,
  TrendingUp, TrendingDown, AlertTriangle, Loader2,
  FlaskConical, Bot, Zap,
} from 'lucide-react'
import { clsx } from 'clsx'
import { useNavigate } from 'react-router-dom'
import TickerLink from '@/components/ui/TickerLink'
import TableSearch from '@/components/ui/TableSearch'
import PageHeader from '@/components/ui/PageHeader'

// ── Helpers ──────────────────────────────────────────────────────────────────

// Backend seeds price/change as null until the first price refresh lands, so
// both formatters must render an em-dash rather than crash on null.
function pct(n: number | null | undefined) {
  if (n == null || !Number.isFinite(n)) return '—'
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`
}
function usd(n: number | null | undefined) {
  if (n == null || !Number.isFinite(n)) return '—'
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const ALERT_TYPE_LABELS: Record<string, string> = {
  price_above: 'Price Above',
  price_below: 'Price Below',
  volume_spike: 'Volume Spike',
  // Legacy labels kept for display of any previously-stored alerts:
  insider_buy: 'Insider Buy',
  congress_trade: 'Congress Trade',
}

// Only alert types the backend checker actually evaluates (price/volume).
// insider_buy / congress_trade are NOT offered: the checker never fires them,
// so offering them would create alerts that can never trigger.
const CREATABLE_ALERT_TYPES = ['price_above', 'price_below', 'volume_spike']

// ── Alert Badge ─────────────────────────────────────────────────────────────

function AlertBadge({ alerts }: { alerts: AlertCondition[] }) {
  const triggered = alerts.filter(a => a.triggered)
  if (triggered.length === 0) {
    return <span className="text-xs text-text-muted">No alerts</span>
  }
  return (
    <div className="flex flex-wrap gap-1">
      {triggered.map((a, i) => (
        <span
          key={i}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-amber-500/15 text-amber-400"
        >
          <AlertTriangle size={10} />
          {ALERT_TYPE_LABELS[a.type] ?? a.type}
        </span>
      ))}
    </div>
  )
}

// ── Alert Status Badge ───────────────────────────────────────────────────────

type AlertStatus = 'TRIGGERED' | 'NEAR' | 'WATCHING'

const STATUS_CONFIG: Record<AlertStatus, { label: string; cls: string; icon?: React.ReactNode }> = {
  TRIGGERED: {
    label: 'Triggered',
    cls: 'bg-rose/15 text-rose-light border border-rose/30',
    icon: <AlertTriangle size={10} />,
  },
  NEAR: {
    label: 'Near',
    cls: 'bg-amber-500/15 text-amber-400 border border-amber-500/30',
    icon: <AlertTriangle size={10} />,
  },
  WATCHING: {
    label: 'Watching',
    cls: 'bg-surface-DEFAULT text-text-muted border border-border',
  },
}

function AlertStatusBadge({
  item,
}: {
  item: import('@/lib/types').WatchlistItem
}) {
  const status = (item.alert_status ?? 'WATCHING') as AlertStatus
  const distance = item.alert_distance_pct
  const cfg = STATUS_CONFIG[status]

  // If no price alerts, fall back to the plain triggered-alert list
  const hasPriceAlerts = item.alerts.some(
    a => a.type === 'price_above' || a.type === 'price_below',
  )
  if (!hasPriceAlerts) {
    return <AlertBadge alerts={item.alerts} />
  }

  return (
    <div className="flex flex-col gap-1">
      <span
        className={clsx(
          'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium w-fit',
          cfg.cls,
        )}
      >
        {cfg.icon}
        {cfg.label}
      </span>
      {distance != null && status !== 'WATCHING' && (
        <span className="text-xs text-text-muted font-mono">
          {distance >= 0
            ? `+${distance.toFixed(1)}% past level`
            : `${Math.abs(distance).toFixed(1)}% to level`}
        </span>
      )}
    </div>
  )
}

// ── Add Ticker Dialog ───────────────────────────────────────────────────────

function AddTickerForm({ onClose }: { onClose: () => void }) {
  const [ticker, setTicker] = useState('')
  const [name, setName] = useState('')
  const [notes, setNotes] = useState('')
  const [alerts, setAlerts] = useState<AlertCondition[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const addAlertCondition = (type: string) => {
    if (alerts.find(a => a.type === type)) return
    const needsValue = type === 'price_above' || type === 'price_below' || type === 'volume_spike'
    setAlerts([...alerts, { type, value: needsValue ? 0 : null, triggered: false }])
  }

  const removeAlert = (idx: number) => {
    setAlerts(alerts.filter((_, i) => i !== idx))
  }

  const updateAlertValue = (idx: number, val: number) => {
    setAlerts(alerts.map((a, i) => i === idx ? { ...a, value: val } : a))
  }

  // Call the API directly (not the store's addItem): a failed add — e.g. a
  // duplicate-ticker 400 — must keep the modal open with an inline error and
  // must NOT set the store-level error, which would replace the whole table
  // with an error panel. The parent's onClose refetches on success.
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!ticker.trim() || !name.trim()) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      await addToWatchlist({ ticker: ticker.trim().toUpperCase(), name: name.trim(), notes, alerts })
      onClose()
    } catch (err) {
      const anyErr = err as { response?: { data?: { detail?: string } }; message?: string }
      setSubmitError(anyErr?.response?.data?.detail ?? anyErr?.message ?? 'Failed to add ticker')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <form
        onSubmit={handleSubmit}
        className="card p-6 w-full max-w-lg space-y-4 shadow-xl"
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-text-primary">Add to Watchlist</h3>
          <button type="button" onClick={onClose} className="text-text-muted hover:text-text-primary">
            <X size={18} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-text-muted mb-1">Ticker</label>
            <input
              value={ticker}
              onChange={e => setTicker(e.target.value)}
              placeholder="e.g. AAPL"
              className="input"
              required
            />
          </div>
          <div>
            <label className="block text-xs text-text-muted mb-1">Company Name</label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Apple Inc."
              className="input"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-xs text-text-muted mb-1">Notes</label>
          <input
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Optional notes..."
            className="input"
          />
        </div>

        {/* Alert conditions */}
        <div>
          <label className="block text-xs text-text-muted mb-2">Alert Conditions</label>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {CREATABLE_ALERT_TYPES.map(type => (
              <button
                key={type}
                type="button"
                onClick={() => addAlertCondition(type)}
                disabled={alerts.some(a => a.type === type)}
                className={clsx(
                  'px-2.5 py-1 rounded-lg text-xs font-medium transition-colors',
                  alerts.some(a => a.type === type)
                    ? 'bg-blue/20 text-blue-light cursor-not-allowed'
                    : 'bg-surface-DEFAULT text-text-secondary hover:bg-surface-card border border-border',
                )}
              >
                + {ALERT_TYPE_LABELS[type]}
              </button>
            ))}
          </div>

          {alerts.length > 0 && (
            <div className="space-y-2">
              {alerts.map((alert, idx) => (
                <div key={idx} className="flex items-center gap-2 bg-surface-DEFAULT rounded-lg px-3 py-2 border border-border">
                  <span className="text-xs font-medium text-text-secondary flex-1">
                    {ALERT_TYPE_LABELS[alert.type]}
                  </span>
                  {(alert.type === 'price_above' || alert.type === 'price_below') && (
                    <input
                      type="number"
                      step="0.01"
                      value={alert.value ?? 0}
                      onChange={e => updateAlertValue(idx, parseFloat(e.target.value) || 0)}
                      className="w-24 px-2 py-1 bg-surface-card border border-border rounded text-xs text-text-primary text-right focus:outline-none focus:ring-1 focus:ring-blue"
                      placeholder="$0.00"
                    />
                  )}
                  {alert.type === 'volume_spike' && (
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        step="0.1"
                        value={alert.value ?? 2}
                        onChange={e => updateAlertValue(idx, parseFloat(e.target.value) || 2)}
                        className="w-16 px-2 py-1 bg-surface-card border border-border rounded text-xs text-text-primary text-right focus:outline-none focus:ring-1 focus:ring-blue"
                      />
                      <span className="text-xs text-text-muted">x avg</span>
                    </div>
                  )}
                  <button type="button" onClick={() => removeAlert(idx)} className="text-text-muted hover:text-rose-light">
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {submitError && (
          <div className="flex items-start gap-2 rounded-lg border border-rose/30 bg-rose/10 px-3 py-2 text-xs text-rose-light">
            <AlertTriangle size={13} className="shrink-0 mt-0.5" />
            <span>{submitError}</span>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="btn-ghost text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || !ticker.trim() || !name.trim()}
            className="btn-primary text-sm gap-2"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            Add to Watchlist
          </button>
        </div>
      </form>
    </div>
  )
}

// ── CSV columns ──────────────────────────────────────────────────────────────

const WATCHLIST_COLUMNS: ColumnDef<WatchlistItem>[] = [
  { header: 'Ticker',       accessor: 'ticker' },
  { header: 'Name',         accessor: 'name' },
  { header: 'Price',        accessor: 'current_price' },
  { header: 'Daily Chg %',  accessor: row => row.daily_change_pct != null ? row.daily_change_pct.toFixed(2) : '' },
  { header: 'Alerts',       accessor: row => row.alerts.filter(a => a.triggered).length },
  { header: 'Notes',        accessor: row => row.notes ?? '' },
]

// ── Page ─────────────────────────────────────────────────────────────────────

export default function WatchlistPage() {
  const {
    items, alerts, loading, error,
    fetchWatchlist, fetchAlerts, removeItem, dismissAlert,
  } = useWatchlist()
  const [showAdd, setShowAdd] = useState(false)
  const [search, setSearch] = useState('')
  const navigate = useNavigate()

  useEffect(() => {
    fetchWatchlist()
    fetchAlerts()
  }, [fetchWatchlist, fetchAlerts])

  const filtered = search
    ? items.filter(i =>
        i.ticker.toLowerCase().includes(search.toLowerCase()) ||
        i.name.toLowerCase().includes(search.toLowerCase())
      )
    : items

  const triggeredCount = items.reduce(
    (n, i) => n + i.alerts.filter(a => a.triggered).length,
    0,
  )

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Watchlist"
        icon={Eye}
        iconClassName="text-violet-400"
        subtitle={
          <>
            {items.length} tickers tracked
            {triggeredCount > 0 && (
              <span className="ml-2 text-amber-400 font-medium">
                {triggeredCount} alert{triggeredCount > 1 ? 's' : ''} triggered
              </span>
            )}
          </>
        }
        actions={
          <>
            <TableSearch placeholder="Search tickers..." onSearch={setSearch} />
            <ExportCSV
              data={filtered}
              columns={WATCHLIST_COLUMNS}
              filename="watchlist"
            />
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="btn-primary text-sm gap-2"
            >
              <Plus size={14} />
              Add Ticker
            </button>
          </>
        }
      />

      {/* Triggered Alerts Banner */}
      {alerts.length > 0 && (
        <div className="card border-amber-500/30 bg-amber-500/10 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Bell size={16} className="text-amber-400" />
            <h3 className="panel-title text-amber-400">Triggered Alerts</h3>
          </div>
          <div className="space-y-1.5">
            {alerts.map((a, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="text-text-primary">
                  <TickerLink ticker={a.ticker} className="font-medium" />
                  {' '}&mdash;{' '}
                  {ALERT_TYPE_LABELS[a.alert_type] ?? a.alert_type}
                  {a.alert_value != null && (
                    <span className="text-text-muted ml-1">({usd(a.alert_value)})</span>
                  )}
                  <span className="text-text-muted ml-2">Current: {usd(a.current_price)}</span>
                </span>
                <button
                  onClick={() => dismissAlert(a.watchlist_id)}
                  className="text-xs text-text-muted hover:text-text-primary flex items-center gap-1"
                >
                  <BellOff size={12} /> Dismiss
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Watchlist Table */}
      <DataSection
        loading={loading}
        error={error ?? undefined}
        empty={!loading && !error && items.length === 0}
        emptyMessage="Add tickers to your watchlist to track prices and set alerts."
        onRetry={fetchWatchlist}
        skeletonRows={5}
      >
        <div className="card overflow-hidden">
          <div className="table-responsive overflow-x-auto">
            <table className="table-pro w-full text-sm">
              <thead>
                <tr>
                  <th className="px-4 py-3">Ticker</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3 !text-right">Price</th>
                  <th className="px-4 py-3 !text-right">Daily Change</th>
                  {/* No 30d Trend column: the backend always returns sparkline: []
                      so the column was permanently empty. Reinstate only when the
                      backend actually sends history — do not fabricate one. */}
                  <th className="px-4 py-3">Alerts</th>
                  <th className="px-4 py-3 !text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(item => {
                  const positive = (item.daily_change_pct ?? 0) >= 0
                  const isTriggered = item.alert_status === 'TRIGGERED'
                  const isNear = item.alert_status === 'NEAR'
                  return (
                    <tr
                      key={item.id}
                      className={clsx(
                        isTriggered && 'bg-rose/5',
                        isNear && 'bg-amber-500/5',
                      )}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <TickerLink ticker={item.ticker} className="font-semibold" />
                          {item.tradeable === false && item.proxy && (item.proxy.adr || item.proxy.etf) && (
                            <a
                              href={`/app/stock/${encodeURIComponent((item.proxy.adr || item.proxy.etf)!)}`}
                              className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border bg-amber-500/10 text-amber-300 border-amber-500/40 font-semibold hover:bg-amber-500/20 transition"
                              title={item.proxy.rationale ?? `${item.tradeability_note ?? ''} — proxy shown`}
                            >
                              via {item.proxy.adr || item.proxy.etf} →
                            </a>
                          )}
                          {item.tradeable === false && !(item.proxy && (item.proxy.adr || item.proxy.etf)) && (
                            <span
                              className="text-[9px] px-1.5 py-0.5 rounded-full border bg-surface-hover text-text-muted border-border"
                              title={item.tradeability_note ?? 'No UK-retail access'}
                            >
                              {item.tradeability_note?.split(' — ')[0] ?? 'GLOBAL'}
                            </span>
                          )}
                          {(item.recent_catalyst_count ?? 0) > 0 && (
                            <a
                              href={`/app/catalysts?ticker=${encodeURIComponent(item.ticker)}`}
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border bg-blue-500/10 text-blue-300 border-blue-500/30 hover:bg-blue-500/20 transition"
                              title={`${item.recent_catalyst_count} catalyst event${item.recent_catalyst_count === 1 ? '' : 's'} in last 7d — latest category: ${item.recent_catalyst_category ?? 'Other'}`}
                            >
                              <Zap size={9} aria-hidden />
                              {item.recent_catalyst_category ?? 'Catalyst'}
                              <span className="opacity-70">{item.recent_catalyst_count}</span>
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-text-secondary">{item.name}</td>
                      <td className="px-4 py-3 text-right font-mono text-text-primary">
                        {usd(item.current_price)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-medium">
                        {item.daily_change_pct == null ? (
                          <span className="text-text-muted">—</span>
                        ) : (
                          <span className={positive ? 'delta-chip-pos' : 'delta-chip-neg'}>
                            {positive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                            {pct(item.daily_change_pct)}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <AlertStatusBadge item={item} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => navigate('/app/chat')}
                            title="Analyze with AI agents"
                            className="p-1.5 rounded-lg text-text-muted hover:text-blue-light hover:bg-blue/10 transition-colors"
                          >
                            <Bot size={14} />
                          </button>
                          <button
                            onClick={() => navigate('/app/backtest')}
                            title="Add to Backtest"
                            className="p-1.5 rounded-lg text-text-muted hover:text-violet-400 hover:bg-violet-500/10 transition-colors"
                          >
                            <FlaskConical size={14} />
                          </button>
                          <button
                            onClick={() => dismissAlert(item.id)}
                            title="Dismiss alerts"
                            className="p-1.5 rounded-lg text-text-muted hover:text-amber-400 hover:bg-amber-500/10 transition-colors"
                          >
                            <BellOff size={14} />
                          </button>
                          <button
                            onClick={() => removeItem(item.id)}
                            title="Remove from watchlist"
                            className="p-1.5 rounded-lg text-text-muted hover:text-rose-light hover:bg-rose/10 transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
                {filtered.length === 0 && items.length > 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-text-muted">
                      No tickers match your search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </DataSection>

      {/* Add Modal */}
      {showAdd && <AddTickerForm onClose={() => { setShowAdd(false); fetchWatchlist() }} />}
    </div>
  )
}
